import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import PackageCard from "@/components/PackageCard";
import SEO from "@/components/SEO";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = `${API}/api/static/hero/packages.jpg`;

const MONTH_LABELS = {
  id: [
    "Januari","Februari","Maret","April","Mei","Juni",
    "Juli","Agustus","September","Oktober","November","Desember",
  ],
  en: [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ],
};

const TIER_LABELS = {
  id: {
    silver: "Silver", gold: "Gold", platinum: "Platinum",
    titanium: "Titanium", plus: "Plus (Wisata)", promo: "Super Promo",
    liburan: "Liburan",
  },
  en: {
    silver: "Silver", gold: "Gold", platinum: "Platinum",
    titanium: "Titanium", plus: "Plus (Tour)", promo: "Super Promo",
    liburan: "Holiday",
  },
};

const PRICE_RANGES = [
  { value: "budget",   min: 0,        max: 29999999,   labelKey: "priceBudget" },
  { value: "standard", min: 30000000, max: 39999999,   labelKey: "priceStandard" },
  { value: "premium",  min: 40000000, max: 59999999,   labelKey: "pricePremium" },
  { value: "luxury",   min: 60000000, max: 9999999999, labelKey: "priceLuxury" },
];

const ALL = "__all__";

export default function Packages() {
  const { t, lang } = useLang();
  const [packages, setPackages] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [tab, setTab] = React.useState(() => {
    if (typeof window === "undefined") return "all";
    const p = new URLSearchParams(window.location.search).get("tab");
    return p === "umrah" || p === "haji" ? p : "all";
  });
  const [tier, setTier] = React.useState(ALL);
  const [month, setMonth] = React.useState(ALL);
  const [airline, setAirline] = React.useState(ALL);
  const [airport, setAirport] = React.useState(ALL);
  const [priceRange, setPriceRange] = React.useState(ALL);

  React.useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  // Derived filter option lists
  const { tierOptions, monthOptions, airlineOptions, airportOptions } = React.useMemo(() => {
    const tiers = new Set();
    const months = new Set();
    const airlines = new Set();
    const airports = new Set();
    for (const p of packages) {
      if (p.tier) tiers.add(p.tier);
      if (p.departure_date) months.add(p.departure_date.slice(0, 7));
      if (p.airline) airlines.add(p.airline);
      if (p.airport) airports.add(p.airport);
    }
    return {
      tierOptions: [...tiers].sort(),
      monthOptions: [...months].sort(),
      airlineOptions: [...airlines].sort(),
      airportOptions: [...airports].sort(),
    };
  }, [packages]);

  // Apply all filters
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return packages.filter((p) => {
      if (tab !== "all" && p.category !== tab) return false;
      if (tier !== ALL && p.tier !== tier) return false;
      if (month !== ALL && (!p.departure_date || !p.departure_date.startsWith(month))) return false;
      if (airline !== ALL && p.airline !== airline) return false;
      if (airport !== ALL && p.airport !== airport) return false;
      if (priceRange !== ALL) {
        const range = PRICE_RANGES.find((r) => r.value === priceRange);
        if (range) {
          if (!p.price_idr || p.price_idr < range.min || p.price_idr > range.max) return false;
        }
      }
      if (q) {
        const hay = [
          p.name_id, p.name_en, p.description_id, p.description_en,
          p.airline, p.airport, p.tier, p.departure,
          ...(p.highlights_id || []), ...(p.highlights_en || []),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [packages, query, tab, tier, month, airline, airport, priceRange]);

  const umrah = packages.filter((p) => p.category === "umrah");
  const haji = packages.filter((p) => p.category === "haji");

  const formatMonth = (ym) => {
    const [y, m] = ym.split("-");
    return `${MONTH_LABELS[lang][parseInt(m, 10) - 1]} ${y}`;
  };

  const resetFilters = () => {
    setQuery("");
    setTab("all");
    setTier(ALL);
    setMonth(ALL);
    setAirline(ALL);
    setAirport(ALL);
    setPriceRange(ALL);
  };

  const activeFilterCount = [tier, month, airline, airport, priceRange].filter((v) => v !== ALL).length + (query ? 1 : 0);

  return (
    <div data-testid="packages-page">
      <SEO
        title="Paket Umrah & Haji Sesuai Sunnah"
        titleEn="Umrah & Hajj Packages"
        description="Pilih paket umrah & haji terlengkap dari BATIK Tour & Travel: Umroh Silver, Gold, Platinum, Titanium, Umroh Plus Turki & Kuala Lumpur, Haji Plus, dan Haji Furoda. Resmi PPIU & PIHK Kementerian Agama RI."
        descriptionEn="Choose from BATIK Tour's complete umrah & hajj packages: Regular, Turkey Plus, Ramadan, Family Umrah, Hajj Plus, and Furoda Hajj. Sunnah-aligned, transparent pricing."
      />
      <section className="relative pt-32 lg:pt-40 pb-8 overflow-hidden isolate">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/50" aria-hidden />
        <div className="pointer-events-none absolute bottom-0 inset-x-0 h-24 bg-gradient-to-b from-transparent to-background" aria-hidden />
        <div className="container-x max-w-3xl relative">
          <p className="overline mb-5">{t("nav.packages")}</p>
          <h1 className="h-display text-primary">{t("packages.title")}</h1>
          <p className="mt-6 text-lg text-muted-foreground">{t("packages.sub")}</p>
        </div>
      </section>

      {/* Search + Filter Bar */}
      <section className="pb-6" data-testid="packages-filters">
        <div className="container-x">
          <div className="bg-card border border-border rounded-2xl p-5 lg:p-6 shadow-sm">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="packages-search-input"
                type="text"
                placeholder={t("packages.search")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-11 pr-11 h-12 bg-background border-border focus-visible:ring-accent"
              />
              {query && (
                <button
                  data-testid="packages-clear-search"
                  onClick={() => setQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Filter selects */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <Select value={tier} onValueChange={setTier} data-testid="filter-tier">
                <SelectTrigger data-testid="filter-tier-trigger" className="h-11 bg-background">
                  <SelectValue placeholder={t("packages.filterTier")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("packages.allTiers")}</SelectItem>
                  {tierOptions.map((tv) => (
                    <SelectItem key={tv} value={tv}>{TIER_LABELS[lang][tv] || tv}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={month} onValueChange={setMonth} data-testid="filter-month">
                <SelectTrigger data-testid="filter-month-trigger" className="h-11 bg-background">
                  <SelectValue placeholder={t("packages.filterMonth")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("packages.allMonths")}</SelectItem>
                  {monthOptions.map((mv) => (
                    <SelectItem key={mv} value={mv}>{formatMonth(mv)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={airline} onValueChange={setAirline} data-testid="filter-airline">
                <SelectTrigger data-testid="filter-airline-trigger" className="h-11 bg-background">
                  <SelectValue placeholder={t("packages.filterAirline")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("packages.allAirlines")}</SelectItem>
                  {airlineOptions.map((av) => (
                    <SelectItem key={av} value={av}>{av}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {airportOptions.length > 1 && (
                <Select value={airport} onValueChange={setAirport} data-testid="filter-airport">
                  <SelectTrigger data-testid="filter-airport-trigger" className="h-11 bg-background">
                    <SelectValue placeholder={t("packages.filterAirport")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>{t("packages.allAirports")}</SelectItem>
                    {airportOptions.map((ap) => (
                      <SelectItem key={ap} value={ap}>{ap}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={priceRange} onValueChange={setPriceRange} data-testid="filter-price">
                <SelectTrigger data-testid="filter-price-trigger" className="h-11 bg-background">
                  <SelectValue placeholder={t("packages.filterPriceRange")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("packages.allPrices")}</SelectItem>
                  {PRICE_RANGES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{t(`packages.${r.labelKey}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Result count + reset */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground" data-testid="filter-results-count">
                <span className="font-semibold text-primary">{filtered.length}</span> {t("packages.resultsFound")}
              </p>
              {activeFilterCount > 0 && (
                <button
                  data-testid="filter-reset-btn"
                  onClick={resetFilters}
                  className="text-xs uppercase tracking-wider font-semibold text-accent hover:text-accent/80 transition-colors inline-flex items-center gap-1.5"
                >
                  <X className="h-3.5 w-3.5" />
                  {t("packages.resetFilters")} ({activeFilterCount})
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Category tabs + Grid */}
      <section className="pb-32" data-testid="packages-list">
        <div className="container-x">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="mb-8 bg-muted/60 p-1 rounded-full inline-flex h-auto" data-testid="packages-tabs">
              <TabsTrigger value="all" data-testid="tab-all" className="rounded-full px-6 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                {t("packages.all")} ({packages.length})
              </TabsTrigger>
              <TabsTrigger value="umrah" data-testid="tab-umrah" className="rounded-full px-6 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                {t("packages.umrah")} ({umrah.length})
              </TabsTrigger>
              <TabsTrigger value="haji" data-testid="tab-haji" className="rounded-full px-6 py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                {t("packages.haji")} ({haji.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value={tab}>
              {filtered.length === 0 ? (
                <div className="text-center py-20" data-testid="filter-empty-state">
                  <p className="text-lg text-muted-foreground">{t("packages.empty")}</p>
                  <button
                    onClick={resetFilters}
                    className="mt-6 btn-primary"
                  >
                    {t("packages.resetFilters")}
                  </button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="packages-grid">
                  {filtered.map((p, i) => (<PackageCard key={p.id} pkg={p} index={i} />))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
